<template>
    <div class="mb-3 select-none rounded-lg bg-black/5 p-3 dark:bg-white/5">
        <div class="flex items-center justify-between text-theme-text">
            <div class="flex items-center">
                <h3 class="m-0 text-sm font-medium">{{ localizations[lang].filter }}</h3>
                <div class="flex px-2">
                    <div
                        v-for="(color, index) in defaultColors"
                        :key="index"
                        v-tooltip.top-start="color.lang"
                        class="mx-2 my-auto flex items-center"
                        :style="{ '--color-code': color.code }"
                    >
                        <input
                            :id="'filterColor-' + color.lang"
                            v-model="checkedColors"
                            :value="color.code"
                            type="checkbox"
                            class="hidden"
                            @change="emitCheckedColors"
                        />
                        <label :for="'filterColor-' + color.lang" :class="getColorLabelClass(color.code)">
                            <span
                                class="h-5 w-5 rounded-full transition-all duration-200 ease-in-out"
                                :style="{ backgroundColor: color.code }"
                            ></span>
                            <span
                                v-if="checkedColors.includes(color.code)"
                                class="absolute h-2 w-2 rounded-full bg-white"
                            ></span>
                        </label>
                    </div>
                </div>
            </div>
            <button
                v-if="copyContentToClipboard.enabled && !disableExport"
                :disabled="highlightIsEmpty"
                type="button"
                class="rounded bg-gradient-to-r from-blue-500 to-blue-700 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                @click="exportAction"
            >
                {{ localizations[lang].export }}
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import downloadFile from '../../../js/utils/downloadFile';
import { uiAPI } from '../../../../src/js/communication';

export default {
    props: {
        highlights: {
            type: Array,
            default: () => [],
        },
        disableExport: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['checked-colors'],
    data() {
        return {
            checkedColors: [],
        };
    },
    computed: {
        ...mapState([
            'title',
            'uiHidden',
            'ui',
            'localizations',
            'lang',
            'selections',
            'defaultColors',
            'issue',
            'embeddedInMobileApp',
            'copyContentToClipboard',
        ]),
        ...mapGetters(['isEpub', 'needsSidebar', 'onlyMobileVisible', 'isPdf', 'highlightColorsCode']),
        highlightIsEmpty() {
            return this.highlights.length === 0;
        },
        getColorLabelClass() {
            return colorCode => {
                const baseClasses =
                    'm-auto relative w-6 h-6 rounded-full cursor-pointer flex items-center justify-center';
                const borderClass = this.checkedColors.includes(colorCode)
                    ? 'border-2 border-gray-600 dark:border-gray-300'
                    : 'border border-gray-300 dark:border-gray-600';
                return `${baseClasses} ${borderClass}`;
            };
        },
    },
    mounted() {
        this.checkedColors = this.highlightColorsCode;
    },
    methods: {
        emitCheckedColors() {
            this.$emit('checked-colors', this.checkedColors);
        },
        exportAction() {
            if (this.highlightIsEmpty) return;
            const hasNotes = this.highlights.some(note => note.annotationText !== null);

            return hasNotes ? this.exportNotes() : this.exportHighlights();
        },
        exportNotes() {
            if (this.highlightIsEmpty) return;

            const notes = this.highlights.reduce((text, selection) => {
                return `${text}${this.localizations[this.lang].highlight}: ${selection.totalText}; ${
                    this.localizations[this.lang].note
                }: ${selection.annotationText}\n\n\n`;
            }, '');

            const fileName = `${this.localizations[this.lang].export}-${this.title}-${this.localizations[this.lang].notes}.txt`;

            this.embeddedInMobileApp
                ? uiAPI.exportText({ text: notes, fileName })
                : downloadFile(notes, 'text/plain', fileName);
        },
        exportHighlights() {
            if (this.highlightIsEmpty) return;

            const highlights = this.highlights.reduce((text, selection) => `${text}${selection.totalText}\n\n\n`, '');

            const fileName = `${this.localizations[this.lang].export}-${this.title}.txt`;

            this.embeddedInMobileApp
                ? uiAPI.exportText({ text: highlights, fileName })
                : downloadFile(highlights, 'text/plain', fileName);
        },
    },
};
</script>
